import { useEffect, useContext, useRef, useState } from "react";
import { Box, Button, CircularProgress, Snackbar, Alert, useTheme, IconButton, Popover } from "@mui/material";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import CheckIcon from "@mui/icons-material/Check";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RecipeDetail from "./RecipeDetail";
import QuickActions from "./RecipeQuickActions";
import { FavoritesContext } from "../contexts/FavoritesContext";
import UserContext from "../contexts/userContext";
import { usePreviewPreloader } from "../hooks/usePreviewPreloader";
// import customScrollbarStyles from "./customScrollbarStyles";

function SelectedRecipeDetail({ recipe, onRecipeUpdate, onRecipeSaved, savedRecipeId }) {
  const { favoritedRecipes } = useContext(FavoritesContext);
  const { user, session } = useContext(UserContext);
  const theme = useTheme();
  const scrollableRef = useRef(null);
  const { fetchPreview } = usePreviewPreloader();
  const [currentRecipe, setCurrentRecipe] = useState(recipe);
  const pollingIntervalRef = useRef(null);
  
  // State for import functionality
  const [isImporting, setIsImporting] = useState(false);
  const [importedRecipeId, setImportedRecipeId] = useState(null); // Store the real recipe ID after import
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  // State for actions popover
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const actionsOpen = Boolean(actionsAnchor);

  // Check if this is a preview (web) recipe
  const isPreviewRecipe = currentRecipe?.id && typeof currentRecipe.id === 'string' && currentRecipe.id.startsWith("preview-");
  const recipeUrl = currentRecipe?.path;
  
  // Determine if the recipe has been imported (either just now, previously saved, or passed as savedRecipeId)
  // savedRecipeId is passed when the recipe URL is found in savedUrls
  const isImported = !!importedRecipeId || !!savedRecipeId;
  
  // Use the appropriate recipe ID for QuickActions (real DB id, not preview-* id)
  const effectiveRecipeId = importedRecipeId || savedRecipeId || currentRecipe?.id;

  // Reset imported state when recipe changes
  useEffect(() => {
    setImportedRecipeId(null);
  }, [recipeUrl]);

  // Handle importing a web recipe to catalog
  const handleImport = async () => {
    if (!user || !session?.access_token || !recipeUrl) {
      setSnackbar({ open: true, message: "Please sign in to save recipes", severity: "warning" });
      return;
    }

    setIsImporting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/import`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ sourceUrl: recipeUrl }),
      });

      const data = await response.json();

      if (response.ok) {
        setImportedRecipeId(data.recipeId);
        // Notify parent about the saved recipe
        if (onRecipeSaved && data.recipeId) {
          onRecipeSaved(recipeUrl, data.recipeId);
        }
        setSnackbar({ 
          open: true, 
          message: `"${data.title}" saved to your catalog!`, 
          severity: "success" 
        });
      } else if (response.status === 409) {
        // Recipe already exists - use the returned ID
        setImportedRecipeId(data.recipeId);
        // Also notify parent
        if (onRecipeSaved && data.recipeId) {
          onRecipeSaved(recipeUrl, data.recipeId);
        }
        setSnackbar({ 
          open: true, 
          message: data.message || "Recipe already in your catalog", 
          severity: "info" 
        });
      } else {
        setSnackbar({ 
          open: true, 
          message: data.message || "Failed to save recipe", 
          severity: "error" 
        });
      }
    } catch (error) {
      console.error("Import error:", error);
      setSnackbar({ 
        open: true, 
        message: "Failed to save recipe. Please try again.", 
        severity: "error" 
      });
    } finally {
      setIsImporting(false);
    }
  };

  // Scroll to top only when a DIFFERENT recipe is selected, not when data updates
  // Track by recipe path (for web previews) or id (for db recipes)
  const recipeIdentity = currentRecipe?.path || currentRecipe?.id;
  useEffect(() => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTo({ top: 0 });
    }
  }, [recipeIdentity]);

  // Poll for image updates if this is a preview recipe (even if it has an image, in case it gets enhanced)
  useEffect(() => {
    const isPreviewRecipe = currentRecipe?.id && typeof currentRecipe.id === 'string' && currentRecipe.id.startsWith("preview-");
    const recipeUrl = currentRecipe?.path;
    const currentImageUrl = currentRecipe?.image_url;

    if (isPreviewRecipe && recipeUrl) {
      let pollCount = 0;
      const maxPolls = 15; // 30 seconds total (15 * 2 seconds)
      
      // Poll every 2 seconds for image updates
      pollingIntervalRef.current = setInterval(async () => {
        pollCount++;
        
        // Stop after max polls
        if (pollCount > maxPolls) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          return;
        }
        
        try {
          const result = await fetchPreview(recipeUrl);
          if (result.success && result.recipe) {
            const newImageUrl = result.recipe.image_url;
            
            // Update if we got a new image or if the image changed
            if (newImageUrl && newImageUrl !== currentImageUrl) {
              // Add cache-busting to force browser reload
              let updatedImageUrl = newImageUrl;
              try {
                // Remove existing cache-bust parameter if present
                const urlWithoutCacheBust = newImageUrl.replace(/[?&]_t=\d+/g, '').replace(/\?$/, '');
                
                if (urlWithoutCacheBust.startsWith('http://') || urlWithoutCacheBust.startsWith('https://')) {
                  const url = new URL(urlWithoutCacheBust);
                  url.searchParams.set('_t', Date.now().toString());
                  updatedImageUrl = url.toString();
                } else {
                  const separator = urlWithoutCacheBust.includes('?') ? '&' : '?';
                  updatedImageUrl = `${urlWithoutCacheBust}${separator}_t=${Date.now()}`;
                }
              } catch (urlError) {
                // Use original URL if parsing fails
                console.debug("Failed to add cache-bust to image URL:", urlError);
              }
              
              const updatedRecipe = { ...result.recipe, image_url: updatedImageUrl };
              setCurrentRecipe(updatedRecipe);
              if (onRecipeUpdate) {
                onRecipeUpdate(updatedRecipe);
              }
              
              // Stop polling once we have an image
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
            }
          }
        } catch (error) {
          // Silently fail - don't spam console
          console.debug("Polling for image update failed:", error);
        }
      }, 2000); // Poll every 2 seconds

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [currentRecipe, fetchPreview, onRecipeUpdate]);

  // Update current recipe when recipe prop changes
  useEffect(() => {
    setCurrentRecipe(recipe);
  }, [recipe]);

  const isDark = theme.palette.mode === "dark";
  
  // Accent colors matching the new design
  const accentPrimary = isDark ? "#BB86FC" : "#6366f1";
  const accentSecondary = isDark ? "#03DAC6" : "#8b5cf6";
  const glowColor = isDark
    ? "rgba(187, 134, 252, 0.2)"
    : "rgba(99, 102, 241, 0.12)";

  return (
    <Box
      sx={{
        height: "100%",
        position: "relative",
      }}
    >
      {/* More options button - top right */}
      <IconButton
        onClick={(e) => setActionsAnchor(e.currentTarget)}
        sx={{
          position: "absolute",
          top: "0.5em",
          right: "1em",
          zIndex: 10,
          width: 36,
          height: 36,
          border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
          background: isDark ? "rgba(30, 30, 35, 0.9)" : "rgba(255, 255, 255, 0.95)",
          borderRadius: "10px",
          boxShadow: `0 2px 8px -2px rgba(0,0,0,${isDark ? "0.3" : "0.1"})`,
          backdropFilter: "blur(8px)",
          transition: "all 0.2s ease",
          "&:hover": {
            background: isDark ? "rgba(50, 50, 55, 0.95)" : "rgba(245, 245, 250, 0.98)",
            transform: "scale(1.05)",
          },
        }}
      >
        <MoreVertIcon sx={{ fontSize: 20, color: theme.palette.text.secondary }} />
      </IconButton>
      
      {/* Actions popover */}
      <Popover
        open={actionsOpen}
        anchorEl={actionsAnchor}
        onClose={() => setActionsAnchor(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
              background: isDark
                ? "rgba(30, 30, 35, 0.98)"
                : "rgba(255, 255, 255, 0.98)",
              borderRadius: "12px",
              boxShadow: `0 8px 32px -4px rgba(0,0,0,${isDark ? "0.4" : "0.15"})`,
              backdropFilter: "blur(12px)",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          {/* Save to Catalog button for preview (web) recipes */}
          {isPreviewRecipe && user && !isImported && (
            <Button
              variant="contained"
              size="small"
              disabled={isImporting}
              onClick={handleImport}
              startIcon={
                isImporting ? (
                  <CircularProgress size={12} sx={{ color: "#fff" }} />
                ) : (
                  <BookmarkAddIcon sx={{ fontSize: 14 }} />
                )
              }
              sx={{
                fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                fontSize: "0.7rem",
                fontWeight: 500,
                textTransform: "none",
                letterSpacing: "-0.01em",
                py: 0.625,
                px: 1.5,
                borderRadius: "8px",
                background: `linear-gradient(135deg, ${accentPrimary}, ${accentSecondary})`,
                border: "none",
                boxShadow: `0 2px 8px -2px ${glowColor}`,
                transition: "all 0.2s ease",
                "&:hover": {
                  background: `linear-gradient(135deg, ${accentPrimary}, ${accentSecondary})`,
                  boxShadow: `0 4px 12px -2px ${glowColor}`,
                },
                "&:disabled": {
                  background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
                  boxShadow: "none",
                },
              }}
            >
              {isImporting ? "saving..." : "save to catalog"}
            </Button>
          )}
          
          {/* Imported badge */}
          {isPreviewRecipe && user && isImported && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                py: 0.75,
                px: 1.5,
                borderRadius: "8px",
                background: isDark
                  ? "rgba(3, 218, 198, 0.12)"
                  : "rgba(16, 185, 129, 0.1)",
                border: `1px solid ${isDark ? "rgba(3, 218, 198, 0.25)" : "rgba(16, 185, 129, 0.2)"}`,
                fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
                fontSize: "0.7rem",
                fontWeight: 500,
                color: isDark ? "#03DAC6" : "#10b981",
              }}
            >
              <CheckIcon sx={{ fontSize: 14 }} />
              saved to catalog
            </Box>
          )}
          
          {/* Quick actions */}
          {(!isPreviewRecipe || isImported) && (
            <QuickActions
              recipe={effectiveRecipeId !== currentRecipe?.id ? { ...currentRecipe, id: effectiveRecipeId } : currentRecipe}
              isFavorited={favoritedRecipes.includes(effectiveRecipeId)}
              showActions={true}
            />
          )}
        </Box>
      </Popover>

      <Box
        ref={scrollableRef}
        sx={{
          height: "100%",
          overflow: "hidden", // Let inner content handle scrolling
          display: "flex",
          flexDirection: "column",
        }}
      >
        <RecipeDetail
          recipe={currentRecipe}
          index={0}
          expanded={true}
          disableExpansion={true}
          onRecipeClick={() => {}}
        />
      </Box>

      {/* Import success/error snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default SelectedRecipeDetail;

